'use client';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

interface UpdateState {
  updates: Record<string, boolean>;
  setUpdate: (key: string, value: boolean) => void;
}

const UpdatesContext = createContext<UpdateState | null>(null);

/** Pass server-evaluated updateValues(); these preferences never enter FlagsProvider. */
export function ProductUpdatesProvider({ updates, children }: { updates: Record<string, boolean>; children: ReactNode }) {
  const [state, setState] = useState(updates);
  useEffect(() => setState(updates), [updates]);
  const setUpdate = useCallback((key: string, value: boolean) => setState((s) => ({ ...s, [key]: value })), []);
  const value = useMemo(() => ({ updates: state, setUpdate }), [state, setUpdate]);
  return <UpdatesContext.Provider value={value}>{children}</UpdatesContext.Provider>;
}

export function useProductUpdate(key: string, fallback = false): boolean {
  const context = useContext(UpdatesContext);
  return context && Object.prototype.hasOwnProperty.call(context.updates, key) ? context.updates[key] : fallback;
}

export function useSetProductUpdate(): (key: string, value: boolean) => void {
  return useContext(UpdatesContext)?.setUpdate ?? noop;
}

function noop() {}
